import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Organization.css';

const apiUrl = process.env.REACT_APP_API_URL;

const Organization = ({ managerName = 'Sudheer' }) => {
  const [users, setUsers] = useState([]);
  const [projectsCount, setProjectsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, projectsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/users`),
        axios.get(`${apiUrl}/api/fetch-projects`)
      ]);

      setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      if (Array.isArray(projectsRes.data)) {
        setProjectsCount(projectsRes.data.length);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization data:', err);
      setError('Failed to load organization data.');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Live update polling interval (every 3 seconds) to reflect user/project additions & deletions
    const intervalId = setInterval(() => {
      fetchData();
    }, 3000);

    const handleFocus = () => fetchData();
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchData]);

  // Function to extract initials from full name
  const getInitials = (name) => {
    if (!name) return 'U';
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return <div className="organization-loading">Loading organization data...</div>;
  }

  if (error) {
    return <div className="organization-error">{error}</div>;
  }

  return (
    <div className="organization-container">
      {/* Top Header Row with Welcome Title & Stat Cards */}
      <div className="organization-header-row">
        <h1 className="organization-welcome-title">Hi {managerName}...</h1>
        <div className="organization-stats-container">
          <div className="org-stat-card">
            <div className="stat-text-group">
              <span className="stat-main-heading">Employees</span>
              {/* <span className="stat-sub-heading">No of employees</span> */}
            </div>
            <span className="stat-value orange-count">{users.length}</span>
          </div>
          <div className="org-stat-card">
            <div className="stat-text-group">
              <span className="stat-main-heading">Projects</span>
              {/* <span className="stat-sub-heading">No of projects</span> */}
            </div>
            <span className="stat-value orange-count">{projectsCount}</span>
          </div>
        </div>
      </div>

      {/* Employee Cards Grid */}
      <div className="organization-grid">
        {users.map((user, index) => (
          <div className="user-card" key={user._id || index}>
            <div className="user-card-header">
              <div className="user-avatar">
                {user.profile_image ? (
                  <img src={user.profile_image} alt={user.full_name} className="avatar-img" />
                ) : (
                  <span className="avatar-initials">{getInitials(user.full_name)}</span>
                )}
              </div>
              <div className="user-info-primary">
                <div className="user-name-row">
                  <h3 className="user-name" title={user.full_name}>{user.full_name}</h3>
                  {user.on_leave && <span className="leave-badge">LEAVE</span>}
                </div>
                <p className="user-job-title" title={user.job_title}>{user.job_title || 'N/A'}</p>
              </div>
            </div>
            <div className="user-card-body">
              <div className="info-row">
                <span className="info-label">Location : </span>
                <span className="info-value">{user.location || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Department : </span>
                <span className="info-value">{user.department || 'N/A'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Email : </span>
                <span className="info-value email-value" title={user.username}>{user.username || 'N/A'}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Organization;
